import React, { useEffect, useMemo, useState } from 'react';
import { parseQuoteNumber, quoteTotals } from './quoteMath';
import './QuoteBuilder.css';

const API = 'https://cms.amservices.eu/api';
const money = value => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
const newLine = key => ({ key, itemId: null, reference: '', description: '', quantity: '1', price: '', vat: '20' });

async function quoteRequest(path, token, options = {}) {
  const response = await fetch(`${API}/${path}`, {
    ...options,
    headers: { 'X-Auth-Token': token, ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    const error = new Error(data.message || 'Henrri est indisponible.');
    error.partialDocumentId = data.partial ? data.documentId : null;
    throw error;
  }
  return data;
}

export default function QuoteBuilder({ user }) {
  const token = user?.token || localStorage.getItem('am_token') || '';
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [client, setClient] = useState(null);
  const [title, setTitle] = useState('Proposition commerciale');
  const [footer, setFooter] = useState('');
  const [lines, setLines] = useState([newLine(1)]);
  const [nextLineKey, setNextLineKey] = useState(2);
  const [articleQuery, setArticleQuery] = useState('');
  const [articleResults, setArticleResults] = useState([]);
  const [searchingArticles, setSearchingArticles] = useState(false);
  const [recents, setRecents] = useState([]);
  const [loadingRecents, setLoadingRecents] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [busyPdf, setBusyPdf] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const totals = useMemo(() => quoteTotals(lines), [lines]);

  useEffect(() => {
    if (!token || client || clientQuery.trim().length < 2) {
      setClientResults([]);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      quoteRequest(`quotes.php?mode=clients&q=${encodeURIComponent(clientQuery.trim())}`, token, { signal: controller.signal })
        .then(data => setClientResults(data.items || []))
        .catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [clientQuery, client, token]);

  const loadRecents = async () => {
    if (!token) return;
    setLoadingRecents(true);
    try {
      const data = await quoteRequest('quotes.php?mode=recents', token);
      setRecents(data.items || []);
    } catch (err) { setError(err.message); }
    finally { setLoadingRecents(false); }
  };

  useEffect(() => { loadRecents(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLine = (key, field, value) => {
    setLines(current => current.map(line => line.key === key
      ? { ...line, [field]: value, itemId: field === 'quantity' ? line.itemId : null }
      : line));
    setCreated(null);
  };

  const addLine = line => {
    setLines(current => {
      const filled = current.length === 1 && !current[0].description.trim();
      return filled ? [{ ...line, key: current[0].key }] : [...current, { ...line, key: nextLineKey }];
    });
    setNextLineKey(value => value + 1);
    setCreated(null);
  };

  const searchArticles = async () => {
    if (articleQuery.trim().length < 2) return;
    setSearchingArticles(true);
    setError('');
    try {
      const data = await quoteRequest(`quotes.php?mode=articles&q=${encodeURIComponent(articleQuery.trim())}`, token);
      setArticleResults(data.items || []);
    } catch (err) { setError(err.message); }
    finally { setSearchingArticles(false); }
  };

  const addArticle = article => {
    addLine({
      itemId: article.id,
      reference: article.reference || '',
      description: article.description || article.reference || '',
      quantity: '1',
      price: String(article.price ?? 0),
      vat: String(article.vat ?? 20),
    });
    setArticleResults([]);
    setArticleQuery('');
  };

  const createQuote = async () => {
    if (!client || !title.trim() || !totals || creating || created) return;
    setCreating(true);
    setMessage('');
    setError('');
    try {
      const data = await quoteRequest('quotes.php', token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: client.id,
          title: title.trim(),
          footer: footer.trim(),
          lines: lines.map(({ itemId, reference, description, quantity, price, vat }) => ({
            itemId, reference, description, quantity: parseQuoteNumber(quantity),
            price: parseQuoteNumber(price), vat: parseQuoteNumber(vat),
          })),
        }),
      });
      setCreated({ id: data.id, number: data.number });
      setMessage(data.message || 'Brouillon créé dans Henrri.');
      loadRecents();
    } catch (err) {
      if (err.partialDocumentId) {
        setCreated({ id: err.partialDocumentId, number: '' });
        loadRecents();
      }
      setError(err.message);
    }
    finally { setCreating(false); }
  };

  const downloadPdf = async (id, number = '') => {
    setBusyPdf(id);
    setError('');
    try {
      const response = await fetch(`${API}/quote_pdf.php?id=${encodeURIComponent(id)}`, { headers: { 'X-Auth-Token': token } });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Téléchargement du PDF impossible.');
      }
      const blob = await response.blob();
      if (blob.type !== 'application/pdf' || blob.size === 0) throw new Error('Le fichier reçu n’est pas un PDF valide.');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `devis-${String(number || id).replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err.message); }
    finally { setBusyPdf(null); }
  };

  const reset = () => {
    setClient(null); setClientQuery(''); setTitle('Proposition commerciale'); setFooter('');
    setLines([newLine(nextLineKey)]); setNextLineKey(value => value + 1);
    setCreated(null); setMessage(''); setError('');
  };

  return <main className="quote-builder">
    <div className="quote-heading">
      <div><span>AM SERVICES · HENRRI</span><h1>Créer un devis</h1><p>Préparez le devis ici, enregistrez un brouillon dans Henrri, puis téléchargez son PDF.</p></div>
      <button type="button" onClick={reset}>Nouveau devis</button>
    </div>
    {error && <p className="quote-alert quote-error" role="alert">{error}</p>}
    {message && <p className="quote-alert quote-success" role="status">{message}</p>}
    <div className="quote-columns">
      <section className="quote-editor" aria-label="Contenu du devis">
        <h2>Informations</h2>
        <label>Client Henrri
          <input value={client ? client.name : clientQuery} onChange={event => { setClient(null); setClientQuery(event.target.value); setCreated(null); }} placeholder="Rechercher un client…" autoComplete="off" />
        </label>
        {!client && clientResults.length > 0 && <div className="quote-suggestions">
          {clientResults.map(item => <button type="button" key={item.id} onClick={() => { setClient(item); setClientQuery(item.name); setClientResults([]); setError(''); }}>
            <strong>{item.name}</strong><small>{item.address}</small>
          </button>)}
        </div>}
        {client && <p className="quote-selected">Client Henrri sélectionné · {client.address || `n°${client.id}`}</p>}
        <label>Objet du devis<input value={title} maxLength={160} onChange={event => { setTitle(event.target.value); setCreated(null); }} /></label>

        <div className="quote-section-title"><h2>Lignes du devis</h2><button type="button" onClick={() => addLine(newLine(nextLineKey))}>+ Ligne libre</button></div>
        <div className="quote-article-search">
          <input aria-label="Rechercher un article Henrri" value={articleQuery} onChange={event => setArticleQuery(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); searchArticles(); } }} placeholder="Ajouter un article Henrri…" />
          <button type="button" onClick={searchArticles} disabled={searchingArticles || articleQuery.trim().length < 2}>{searchingArticles ? 'Recherche…' : 'Rechercher'}</button>
        </div>
        {articleResults.length > 0 && <div className="quote-suggestions quote-article-results">
          {articleResults.map(item => <button type="button" key={item.id} onClick={() => addArticle(item)}>
            <strong>{item.description || item.reference}</strong><small>{item.reference} · {money(item.price)} HT</small>
          </button>)}
        </div>}
        {lines.map((line, index) => <div className="quote-line-editor" key={line.key}>
          <div className="quote-line-heading"><strong>Ligne {index + 1}{line.itemId ? ' · article Henrri' : ''}</strong><button type="button" onClick={() => { setLines(current => current.filter(item => item.key !== line.key)); setCreated(null); }} disabled={lines.length === 1}>Supprimer</button></div>
          <div className="quote-line-grid">
            <label className="quote-wide">Désignation<input value={line.description} onChange={event => changeLine(line.key, 'description', event.target.value)} maxLength={500} placeholder="Prestation ou article" /></label>
            <label>Référence<input value={line.reference} onChange={event => changeLine(line.key, 'reference', event.target.value)} maxLength={80} /></label>
            <label>Quantité<input inputMode="decimal" value={line.quantity} onChange={event => changeLine(line.key, 'quantity', event.target.value)} /></label>
            <label>Prix unitaire HT (€)<input inputMode="decimal" value={line.price} onChange={event => changeLine(line.key, 'price', event.target.value)} /></label>
            <label>TVA (%)<input inputMode="decimal" value={line.vat} onChange={event => changeLine(line.key, 'vat', event.target.value)} /></label>
          </div>
        </div>)}
        <label>Conditions ou note de bas de page<textarea value={footer} maxLength={1000} onChange={event => { setFooter(event.target.value); setCreated(null); }} rows={3} placeholder="Conditions du devis, délai, modalités…" /></label>
        <div className="quote-submit">
          <button type="button" className="quote-primary" onClick={createQuote} disabled={!client || !title.trim() || !totals || creating || Boolean(created)}>{creating ? 'Création dans Henrri…' : created ? 'Brouillon créé' : 'Créer le brouillon dans Henrri'}</button>
          {created && <button type="button" onClick={() => downloadPdf(created.id, created.number)} disabled={busyPdf === created.id}>{busyPdf === created.id ? 'Préparation du PDF…' : 'Télécharger le PDF Henrri'}</button>}
        </div>
        <p className="quote-help">Le devis reste provisoire dans Henrri. Vérifiez le PDF et finalisez-le ensuite dans Henrri.</p>
      </section>

      <aside className="quote-preview-area">
        <div className="quote-preview" aria-label="Aperçu du devis">
          <div className="quote-preview-top"><img src="/assets/amservices-logo.png" alt="AM Services" /><div><span>DEVIS · APERÇU</span><strong>{created?.number || 'Brouillon'}</strong><small>{new Date().toLocaleDateString('fr-FR')}</small></div></div>
          <div className="quote-preview-rule" />
          <div className="quote-preview-client"><small>DESTINATAIRE</small><strong>{client?.name || 'Choisissez un client Henrri'}</strong><span>{client?.address || ''}</span></div>
          <h2>{title || 'Objet du devis'}</h2>
          <div className="quote-preview-table-wrap"><table><thead><tr><th>Désignation</th><th>Qté</th><th>PU HT</th><th>Total HT</th></tr></thead><tbody>
            {lines.map((line, index) => <tr key={line.key}><td>{line.description || 'Désignation à compléter'}{line.reference && <small>{line.reference}</small>}</td><td>{line.quantity || '—'}</td><td>{parseQuoteNumber(line.price) === null ? '—' : money(parseQuoteNumber(line.price))}</td><td>{totals ? money(totals.details[index].ht) : '—'}</td></tr>)}
          </tbody></table></div>
          <div className="quote-preview-totals"><div><span>Total HT</span><strong>{totals ? money(totals.ht) : '—'}</strong></div><div><span>TVA</span><strong>{totals ? money(totals.tax) : '—'}</strong></div><div className="quote-preview-grand"><span>Total TTC</span><strong>{totals ? money(totals.ttc) : '—'}</strong></div></div>
          {footer && <p className="quote-preview-footer">{footer}</p>}
          <p className="quote-preview-disclaimer">Aperçu CRM · le PDF téléchargé utilise la présentation Henrri.</p>
        </div>

        <section className="quote-recents">
          <div className="quote-section-title"><h2>Devis Henrri récents</h2><button type="button" onClick={loadRecents} disabled={loadingRecents}>{loadingRecents ? 'Chargement…' : 'Actualiser'}</button></div>
          {recents.length === 0 ? <p>Aucun devis à afficher.</p> : <ul>{recents.map(quote => <li key={quote.id}>
            <div><strong>{quote.number || `Brouillon n°${quote.id}`}</strong><span>{quote.client || quote.title}</span></div>
            <button type="button" onClick={() => downloadPdf(quote.id, quote.number)} disabled={busyPdf === quote.id}>{busyPdf === quote.id ? '…' : 'PDF'}</button>
          </li>)}</ul>}
        </section>
      </aside>
    </div>
  </main>;
}
